import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.number.constructor.js";
import React from 'react';
import { useTranslation } from 'react-i18next';
import Panel from 'components/Panel';
import FormField from 'components/FormField';
import FormStack from 'components/FormStack';
import Icon from 'components/Icon';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import TextInput from 'components/TextInput';
import TextArea from 'components/TextArea';
import { ImageField, TagField, AssetTypeField, LanguageField, MarkAsDoneModeField, CompletionTimeField, formatTags, CustomDataField, OwningTeamField } from 'components/FormFields';
import { TYPE_ASSET_THUMBNAIL } from 'utils/file';
import config from 'utils/config';
import getContentItemType from 'utils/asset/getContentItemType';
import { ASSET_WRITE, hasPrivilege } from 'utils/privileges';
function useDescriptionShortHint(_ref) {
  var description = _ref.description,
    aiSuggestion = _ref.aiSuggestion;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  if (!aiSuggestion) {
    return null;
  }
  if (aiSuggestion.status === 'pending') {
    return /*#__PURE__*/React.createElement(Text, {
      size: "xs"
    }, /*#__PURE__*/React.createElement(Icon, {
      vAlign: true,
      name: "sparkles",
      mr: "xxs"
    }), t('ai_summary_generating'));
  } else if (aiSuggestion.status === 'error') {
    return /*#__PURE__*/React.createElement(Text, {
      size: "xs"
    }, /*#__PURE__*/React.createElement(Icon, {
      vAlign: true,
      name: "sparkles",
      mr: "xxs"
    }), t('ai_summary_error'));
  } else if (aiSuggestion.status === 'ready') {
    return /*#__PURE__*/React.createElement(Text, {
      size: "xs"
    }, /*#__PURE__*/React.createElement(Icon, {
      vAlign: true,
      name: "sparkles",
      mr: "xxs"
    }), t('ai_summary_available'), ' ', /*#__PURE__*/React.createElement(TextLink, {
      bold: true,
      as: "button",
      onClick: function onClick() {
        return description.set("".concat(description.value, " ").concat(aiSuggestion.value));
      }
    }, t('actions_insert')));
  }
}
function GeneralDetailsPanel(_ref2) {
  var _markAsDoneMode;
  var fields = _ref2.fields,
    content = _ref2.content,
    aiSuggestions = _ref2.aiSuggestions,
    isScormCourse = _ref2.isScormCourse,
    isDistributed = _ref2.isDistributed;
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  var name = fields.name,
    description = fields.description,
    tags = fields.tags,
    customType = fields.customType,
    locale = fields.locale,
    completionTime = fields.completionTime,
    thumbnailId = fields.thumbnailId,
    externalRefId = fields.externalRefId,
    ownerTeamId = fields.ownerTeamId;
  var markAsDoneMode;
  if ('markAsDoneMode' in fields) {
    markAsDoneMode = fields.markAsDoneMode;
  }
  var contentId = content === null || content === void 0 ? void 0 : content.id;
  var descriptionHintContent = useDescriptionShortHint({
    description: description,
    aiSuggestion: aiSuggestions === null || aiSuggestions === void 0 ? void 0 : aiSuggestions.description
  });
  return /*#__PURE__*/React.createElement(Panel, null, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(FormField, {
    required: true,
    label: "".concat(t('form_title'), " *"),
    alert: name.wasChanged ? name.error : undefined
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: name.value,
    onChange: function onChange(e) {
      return name.set(e.target.value);
    },
    disabled: isDistributed
  })), /*#__PURE__*/React.createElement(ImageField, {
    id: "assetCoverImage",
    thumbnailId: thumbnailId.value,
    onChange: function onChange(e) {
      return thumbnailId.set(e);
    },
    isDisabled: isDistributed,
    assetType: customType.value,
    contentType: TYPE_ASSET_THUMBNAIL,
    contentId: contentId,
    contentName: name.value,
    contentDescription: description.value
  }), /*#__PURE__*/React.createElement(FormField, {
    label: t('form_description'),
    shortHint: descriptionHintContent
  }, /*#__PURE__*/React.createElement(TextArea, {
    maxRows: 8,
    minRows: 3,
    value: description.value,
    onChange: function onChange(e) {
      return description.set(e.target.value);
    },
    disabled: isDistributed
  })), /*#__PURE__*/React.createElement(TagField
  // Format tags to have all required information for correctly displaying the tag pills
  , {
    selectedTags: formatTags(tags.value),
    onChangeSelectedTags: function onChangeSelectedTags(selectedItems) {
      tags.set(selectedItems.map(function (tag) {
        return {
          name: tag.name,
          id: tag.id || ''
        };
      }));
    },
    isDisabled: isDistributed
  }), /*#__PURE__*/React.createElement(AssetTypeField, {
    type: customType.value,
    onChange: function onChange(newType) {
      return customType.set(newType);
    },
    originalType: getContentItemType(content || {}, false),
    isDisabled: isDistributed
  }), config.assetLanguageEnabled && /*#__PURE__*/React.createElement(LanguageField, {
    value: locale.value,
    onChange: function onChange(newLocale) {
      return locale.set(newLocale);
    },
    isDisabled: isDistributed
  }), /*#__PURE__*/React.createElement(CompletionTimeField, {
    completionTime: Number(completionTime.value),
    onChange: function onChange(e) {
      return completionTime.set(Number(e.target.value));
    },
    isDisabled: isDistributed
  }), config.autoMarkAsDone && /*#__PURE__*/React.createElement(MarkAsDoneModeField, {
    isScormCourse: isScormCourse,
    mode: (_markAsDoneMode = markAsDoneMode) === null || _markAsDoneMode === void 0 ? void 0 : _markAsDoneMode.value,
    onChange: function onChange(newMode) {
      var _markAsDoneMode2;
      return (_markAsDoneMode2 = markAsDoneMode) === null || _markAsDoneMode2 === void 0 ? void 0 : _markAsDoneMode2.set(newMode);
    }
  }), !isDistributed && config.managersCreateAssets && hasPrivilege(ASSET_WRITE) && /*#__PURE__*/React.createElement(OwningTeamField, {
    ownerTeamId: ownerTeamId.value,
    contentId: contentId,
    onChange: function onChange(teams) {
      return ownerTeamId === null || ownerTeamId === void 0 ? void 0 : ownerTeamId.set(teams.selectedItems.id);
    },
    onDeleteOwningTeam: function onDeleteOwningTeam() {
      return ownerTeamId === null || ownerTeamId === void 0 ? void 0 : ownerTeamId.set(null);
    }
  }), config.enableContentExternalReference && hasPrivilege(ASSET_WRITE) && /*#__PURE__*/React.createElement(CustomDataField, {
    customData: externalRefId.value || '',
    onChange: function onChange(e) {
      return externalRefId === null || externalRefId === void 0 ? void 0 : externalRefId.set(e.target.value);
    }
  })));
}
export default GeneralDetailsPanel;